import React, { useState } from 'react';
import axios from 'axios';
import LoadingSpinner from '../components/LoadingSpinner';
import { useNavigate } from 'react-router-dom';

import { dateToMonDDYYYY } from '../utils/dateUtils';
import defaultCteLogo from '../assets/acuity-cte-logo.png';

const LEGACY_LOGO_URL = 'https://acuitycte-files.s3.us-west-2.amazonaws.com/public/acuity-tech-logo.png';

const isGlobalAccessCard = (card) => !card || card.global === true || card.school_id == null;

const resolveLogoUrl = (card) => {
  if (isGlobalAccessCard(card)) return defaultCteLogo;
  const schoolLogo = card?.school?.logo_url;
  if (schoolLogo && schoolLogo !== LEGACY_LOGO_URL) return schoolLogo;
  return defaultCteLogo;
};

const Dashboard = ({ userData, setUserData }) => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Remove the component-level auth check - let PrivateRoute handle it
  // if (!userData) {
  //   return (
  //     <div style={styles.container}>
  //       <p>Please log in to view your dashboard.</p>
  //     </div>
  //   );
  // }

  const defaultAccessCard = userData.accesscards?.find(
    ac => ac.accesscard_id === userData.default_accesscard_id
  );

  const handleAccessCardClick = async (accesscard_id) => {
    if (accesscard_id === userData.default_accesscard_id) {
      return; // Already the default
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put('/api/user/default-accesscard', 
        { accesscard_id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setUserData(response.data);
    } catch (error) {
      console.error('Error updating default access card:', error);
      alert('Failed to update default access card. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      {loading && (
        <div style={styles.loadingOverlay}>
          <LoadingSpinner />
        </div>
      )}
      
      
      
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Account Information</h2>
        <div style={styles.infoGrid}>
          <div style={styles.infoItem}>
            <label style={styles.label}>Name:</label>
            <span style={styles.value}>{userData.first_name} {userData.last_name}</span>
          </div>
          <div style={styles.infoItem}>
            <label style={styles.label}>Email:</label>
            <span style={styles.value}>{userData.primary_email}</span>
          </div>
          <div style={styles.infoItem}>
            <label style={styles.label}>Default Role:</label>
            <span style={styles.value}>{defaultAccessCard?.role || 'N/A'}</span>
          </div>
        </div>
      </div>

      <div id="accesscardsSection" style={styles.section}>
        <h2 style={styles.sectionTitle}>Access Cards</h2>
        {userData.accesscards?.map((card, index) => (
          <div 
            key={index} 
            style={{
              ...styles.card,
              ...(card.accesscard_id === userData.default_accesscard_id ? styles.defaultCard : styles.clickableCard)
            }}
            onClick={() => handleAccessCardClick(card.accesscard_id)}
            onMouseEnter={(e) => {
              if (card.accesscard_id !== userData.default_accesscard_id) {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
              }
            }}
            onMouseLeave={(e) => {
              if (card.accesscard_id !== userData.default_accesscard_id) {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
              }
            }}
          >
            <div style={styles.cardHeader}>
              <div style={styles.cardTitleSection}>
                <img
                  src={resolveLogoUrl(card)}
                  alt={isGlobalAccessCard(card) ? 'Acuity Career Technical Education' : `${card.school?.school_name || 'School'} Logo`}
                  style={styles.schoolLogo}
                />
                <h3 style={styles.cardTitle}>
                  {card.school?.school_name} - {card.role}
                </h3>
              </div>
              {card.accesscard_id === userData.default_accesscard_id && (
                <span style={styles.defaultBadge}>Default</span>
              )}
            </div>
            <div style={styles.cardContent}>
              <p style={styles.cardEmail}>{card.email}</p>
              {card.role === "Student" && (
                <div id={`enrollmentsBox_${card.accesscard_id}`} style={styles.enrollments}>
                  <h4 style={styles.enrollmentsTitle}>Enrollments:</h4>
                  {card.enrollments?.map((enrollment, idx) => (
                    <div id={`enrollmentBox_${enrollment.enrollment_id}`} key={idx} style={styles.enrollment}>
                      <span>Start: {dateToMonDDYYYY(enrollment.start_date)}</span>
                      {enrollment.end_date && (
                        <span>End: {dateToMonDDYYYY(enrollment.end_date)}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const styles = {
  container: {
    padding: '2rem',
    maxWidth: '1000px',
    margin: '0 auto',
    position: 'relative'
  },
  loadingOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 9999
  },
  title: {
    fontSize: '2.5rem',
    color: '#333',
    marginBottom: '2rem'
  },
  section: {
    backgroundColor: 'white',
    padding: '2rem',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    marginBottom: '2rem'
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.5rem'
  },
  sectionTitle: {
    fontSize: '1.8rem',
    color: '#333',
    margin: 0
  },
  updateButton: {
    padding: '0.5rem 1rem',
    backgroundColor: '#007bff',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontWeight: '500',
    transition: 'background-color 0.3s ease'
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '1.5rem'
  },
  infoItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem'
  },
  label: {
    fontSize: '0.9rem',
    color: '#666',
    textTransform: 'uppercase'
  },
  value: {
    fontSize: '1.1rem',
    color: '#333'
  },
  card: {
    borderRadius: '8px',
    padding: '1.5rem',
    marginBottom: '1rem',
    transition: 'all 0.3s ease',
  },
  defaultCard: {
    border: '1px solid #28a745',
    backgroundColor: '#f8fff9',
  },
  clickableCard: {
    border: '1px solid #ddd',
    cursor: 'pointer',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
    },
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem'
  },
  cardTitleSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem'
  },
  schoolLogo: {
    height: '40px',
    width: 'auto',
    objectFit: 'contain',
    borderRadius: '4px'
  },
  cardTitle: {
    fontSize: '1.3rem',
    color: '#333',
    margin: 0
  },
  defaultBadge: {
    backgroundColor: '#28a745',
    color: 'white',
    padding: '0.25rem 0.75rem',
    borderRadius: '4px',
    fontSize: '0.9rem'
  },
  cardContent: {
    color: '#666'
  },
  cardEmail: {
    marginBottom: '1rem'
  },
  enrollments: {
    marginTop: '1rem'
  },
  enrollmentsTitle: {
    fontSize: '1.1rem',
    color: '#333',
    marginBottom: '0.5rem'
  },
  enrollment: {
    display: 'flex',
    gap: '1rem',
    fontSize: '0.9rem',
    color: '#666',
    marginBottom: '0.5rem'
  }
};


export default Dashboard; 